/* eslint-disable no-underscore-dangle */
/* eslint-disable max-classes-per-file */
import { uuid } from "../../utils";
import * as eventParser from "./parses/event";
import * as conditionParser from "./parses/condition";
import * as targetParser from "./parses/target";
import * as paramsParser from "./parses/params";
import * as requestParser from "./parses/request";
import * as triggerParser from "./parses/trigger";

class Listener {
  constructor(options = {}) {
    this.type = options.type;
    this.uuid = options.uuid || uuid();
    this.fieldApi = options.fieldApi;
    this.config = options.config;
    this.event = options.event;
    this.func = options.func;
    this.callbackMap = {};
  }

  on = () => {
    Object.keys(this.event).forEach((fieldId) => {
      // 字段不存在，退出
      if (!this.fieldApi.isFieldExist(fieldId)) return;

      this.event[fieldId].forEach((e) => {
        const key = `${fieldId}:${e}`;
        if (!this.callbackMap[key]) {
          if (e === "change") {
            // 如果配置了 disableChangeEvent，则不通过 change 事件触发联动
            if (this.config.disableChangeEvent) return true;
            this.callbackMap[key] = this.fieldApi.onValueChange(
              fieldId,
              this.run,
              this.config
            );
          } else if (
            ["onRequest", "onTriggerRequest", "onOpenIframeModal"].includes(e)
          ) {
            this.callbackMap[key] = this.fieldApi.bindFieldEvent(
              fieldId,
              e,
              this.run,
              this.config
            );
            if (!this.fieldApi.getFieldProp(fieldId, e)) {
              this.fieldApi.setFieldProp(fieldId, e, true);

              // 将事件参数设置到对应的表单项中
              e === "onOpenIframeModal" &&
                this.fieldApi.setFieldProp(
                  fieldId,
                  "onOpenIframeModalData",
                  this.config
                );
            }
          }
        }
      });
    });
  };

  off = () => {
    Object.keys(this.callbackMap).forEach((key) => {
      if (typeof this.callbackMap[key] === "function") {
        this.callbackMap[key]();
      }
      delete this.callbackMap[key];
    });
  };

  /**
   * 解析成 props 方法
   * Form/index.js 调用 this.formContext.setGlobalEvents(this.resolverManager.toProps());
   */
  toProps = () => {
    const event = {};
    Object.keys(this.event).forEach((fieldId) => {
      event[fieldId] = {};

      this.event[fieldId].forEach((e) => {
        if (e === "change") return;

        event[fieldId][e] = (...rest) => {
          // 兼容以前的调用（第一个参数为undefined），并支持全局事件（如onEditModalShow事件）调用时传入特定参数
          this.run(undefined, rest && rest[1]);
        };
      });
    });

    return event;
  };

  run = (...rest) => {
    this.func(...rest);
  };
}

class BaseResolver {
  listeners = [];

  constructor(options = {}) {
    this.owner = options.owner;
    this.schema = options.schema;
    this.context = options.context;
    this.fieldApi = options.fieldApi;
    this.funcs = options.funcs;
    this.isDetail = options.isDetail;
    this.isEdit = options.isEdit;
    this.disableDevicePlugin = options.disableDevicePlugin;
    this.params = options.params;
    this.updateRequestCounter = options.updateRequestCounter;
  }

  /**
   * 混合事件对象
   */
  _mergeEvent = (source = {}, ...rest) => {
    const result = { ...source };
    (rest || []).forEach((item) => {
      item &&
        Object.keys(item).forEach((fieldId) => {
          if (!result[fieldId]) result[fieldId] = [];
          item[fieldId].forEach((e) => {
            if (!result[fieldId].includes(e)) {
              result[fieldId].push(e);
            }
          });
        });
    });

    return result;
  };

  /**
   * 解析条件模板
   * @param {string} template 条件模板
   */
  _parseCondition = (template) => {
    return conditionParser.parse(template, {
      fieldApi: this.fieldApi,
      funcs: this.funcs,
    });
  };

  /**
   * 解析事件模板
   * @param {string} template 事件模板
   */
  _parseEvent = (template) => {
    return eventParser.parse(template, {
      fieldApi: this.fieldApi,
      funcs: this.funcs,
    });
  };

  /**
   * 解析目标对象
   * @param {object} target 目标对象
   */
  _parseTarget(target, options) {
    return targetParser.parse(target, {
      ...options,
      fieldApi: this.fieldApi,
      funcs: this.funcs,
      isDetail: this.isDetail,
      isEdit: this.isEdit,
    });
  }

  /**
   * 解析请求参数
   * @param {object} params 请求参数
   */
  _parseParams(params) {
    return paramsParser.parse(params, {
      fieldApi: this.fieldApi,
      funcs: this.funcs,
    });
  }

  /**
   * 解析并行处理的requests
   * @param {object} params 请求参数
   */
  _parseRequest(params) {
    return requestParser.parse(params, {
      owner: this.owner,
    });
  }

  /**
   * 解析triggers
   * @param {object} params 请求参数
   */
  _parseTrigger(params) {
    return triggerParser.parse(params, {
      fieldApi: this.fieldApi,
    });
  }

  /**
   * 创建回调
   */
  _addListener = (options) => {
    const listener = new Listener({
      uuid: uuid(),
      ...options,
      owner: this,
      type: this._type,
      fieldApi: this.fieldApi,
    });
    this.listeners.push(listener);

    // 将 listener 回注到 ResolverManager 中
    this.owner.listenersMap[listener.uuid] = listener;
  };

  apply = () => {
    this.listeners.forEach((listener) => {
      const { initial = true, reExecute = true } = listener.config;
      if (this.isEdit) {
        // 表单处于编辑态时，reExecute 为 true 才会执行
        if (initial && reExecute) listener.run({ initial });
      } else if (initial) {
        listener.run();
      }
      listener.on();
    });
  };

  rebind = () => {
    this.listeners.forEach((listener) => {
      listener.off();
      listener.on();
    });
  };

  /**
   * 获取指定回调
   */
  getListenerById = (id) => {
    return this.listeners.find((listener) => listener.uuid === id);
  };

  /**
   * 执行指定回调
   */
  execListener = (id) => {
    const listener = this.getListenerById(id);
    if (listener) listener.run();
  };
}

export default BaseResolver;
